import {
  ZipClassTableData,
  ZipCodeTableData,
  ZipProjectAction,
  ZipProjectActionsEnum,
  ZipProjectModuleRoutesEnum
} from "@app/projects/zip-projects/interfaces";
import { ProjectModule } from "@app/projects/literals";
import { ProjectPerspective, ProjectRunConfigurationType } from "@app/projects/enumerators";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { formatDateTime } from "@app/shared/formatDateTime";
import { wafflesKeyColumnName } from "@app/shared/waffles/exports";
import { ZipClassSortableColumnEnum, ZipCodeSortableColumnEnum } from "@app/projects/zip-projects/interfaces/zipAnalysisSortableColumns";
import { ZipAnalysisSearchSelections } from "@app/projects/zip-projects/interfaces/zipAnalysisSearchSelections";
import { ZipCodeDisplayModel } from "@app/projects/zip-projects/interfaces/zipCodeDisplayModel";
import { SpecialtyGroupException } from "@app/exceptions/interfaces";
import { querystringify } from "@app/shared/utility/querystringify";
import { ZipAnalysisSearchParameters } from "@app/projects/zip-projects/interfaces/zipAnalysisSearchParameters";
import AdequacyFilters from "@app/projects/network-assessment/adequacy/adequacyFilters";
import { ZipCodeTrendChartItem, ZipClassTrendChartItem } from "./interfaces/zipTrendChartItems";
import { TrendChartItemType } from "@app/velvet/enumerators";
import Project from "@app/projects/project";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import Features from "@app/feature-flags/features";
import { ZipContextState } from "@app/projects/zip-projects/interfaces/zipContextState";

export const createDummyDropdownItem = (value: string): DropdownItem<string> => {
  return {
    display: value,
    key: value,
    name: value,
  };
}

export const onRunProject = (
  dispatch: (value: ZipProjectAction) => void,
  onRunProject: (projectId: number) => void,
  projectId: number
): void => {
  dispatch({
    payload: { loading: true },
    type: ZipProjectActionsEnum.Loading,
  });
  onRunProject(projectId);
}

export const translateUrlModuleToProjectModule = (urlModule: ZipProjectModuleRoutesEnum): ProjectModule => {
  let newModule: ProjectModule;
  switch (urlModule.toLowerCase()) {
    case ZipProjectModuleRoutesEnum.Adequacy:
      newModule = "Adequacy";
      break;
    case ZipProjectModuleRoutesEnum.Summary:
      newModule = "Summary";
      break;
    case ZipProjectModuleRoutesEnum.NetworkIntegrity:
      newModule = "Network-Integrity";
      break;
    case ZipProjectModuleRoutesEnum.ProviderHub:
      newModule = "Provider-Hub";
      break;
    default:
      newModule = "Adequacy";
      break;
  }
  return newModule;
}

// Provider Hub for Zip projects requires Snowflake servicing-provider data for the run
// configuration in addition to the flags and the same run-configuration-state
// restrictions used for Maps, mirroring the gating in CountyProjectOverview.tsx.
export const shouldShowZipProviderHub = (enabledFeatureFlags: Set<string>, state: ZipContextState): boolean => {
  return enabledFeatureFlags.has(Features.PROVIDER_HUB)
    && enabledFeatureFlags.has(Features.PROVIDER_HUB_ZIP_PROJECTS)
    && !!state.runConfiguration
    && !!state.runConfiguration.isServicingProviderInSnowflake
    && !!state.runConfiguration.isLatestPublished
    && !state.runConfiguration.isTermed
    && !state.runConfiguration.isProjected
    && !state.runConfiguration.isProjectedTermed
    && !state.isDualMembershipMode
    && !state.isCompareMode;
}

export const isProjectRunning = (project: Project): boolean => {
  return !project
    || project.isRunning
    || project.isReadOnly;
}

const getZipCodeSortedColumnFromEnum = (newColumn: string): ZipCodeSortableColumnEnum => {
  let sortedColumn: ZipCodeSortableColumnEnum;
  switch (newColumn) {
    /* zip code */
    case "state":
      sortedColumn = ZipCodeSortableColumnEnum.StateName;
      break;
    case "county":
      sortedColumn = ZipCodeSortableColumnEnum.County;
      break;
    case "city":
      sortedColumn = ZipCodeSortableColumnEnum.City;
      break;
    case "zipClass":
      sortedColumn = ZipCodeSortableColumnEnum.ZipClass;
      break;
    case "zipCode":
      sortedColumn = ZipCodeSortableColumnEnum.ZipCode;
      break;
    case "specialty":
      sortedColumn = ZipCodeSortableColumnEnum.SpecialtyGroupCode;
      break;
    case "minProviderCount":
      sortedColumn = ZipCodeSortableColumnEnum.MinProviderCount;
      break;
    case "reqDist":
      sortedColumn = ZipCodeSortableColumnEnum.ReqDist;
      break;
    case "reqTime":
      sortedColumn = ZipCodeSortableColumnEnum.ReqTime;
      break;
    case "minPassPercent":
      sortedColumn = ZipCodeSortableColumnEnum.MinPassPercent;
      break;
    case "metPassPercent":
      sortedColumn = ZipCodeSortableColumnEnum.MetPassPercent;
      break;
    case "metTime":
      sortedColumn = ZipCodeSortableColumnEnum.MetTime;
      break;
    case "metDist":
      sortedColumn = ZipCodeSortableColumnEnum.MetDist;
      break;
    case "membershipCount":
      sortedColumn = ZipCodeSortableColumnEnum.MembershipCount;
      break;
    case "providerCount":
      sortedColumn = ZipCodeSortableColumnEnum.ProviderCount;
      break;
    case "memberCountWithAccess":
      sortedColumn = ZipCodeSortableColumnEnum.MemberCountWithAccess;
      break;
    case "withAccessPercent":
      sortedColumn = ZipCodeSortableColumnEnum.WithAccessPercent;
      break;
    case "memberCountWithoutAccess":
      sortedColumn = ZipCodeSortableColumnEnum.MemberCountWithoutAccess;
      break;
    case "withoutAccessPercent":
      sortedColumn = ZipCodeSortableColumnEnum.WithoutAccessPercent;
      break;
    case "avgDist":
      sortedColumn = ZipCodeSortableColumnEnum.AverageDistance;
      break;
    case "avgTime":
      sortedColumn = ZipCodeSortableColumnEnum.AverageTime;
      break;
    case "maxDist":
      sortedColumn = ZipCodeSortableColumnEnum.MaximumDistance;
      break;
    case "maxTime":
      sortedColumn = ZipCodeSortableColumnEnum.MaximumTime;
      break;
    case "avgDist2":
      sortedColumn = ZipCodeSortableColumnEnum.AverageDistance2;
      break;
    case "avgDistWith":
      sortedColumn = ZipCodeSortableColumnEnum.AverageDistanceWithAccess;
      break;
    case "avgDistWithout":
      sortedColumn = ZipCodeSortableColumnEnum.AverageDistanceWithoutAccess;
      break;
    case "maxDist2":
      sortedColumn = ZipCodeSortableColumnEnum.MaximumDistance2;
      break;
    case "maxDistWith":
      sortedColumn = ZipCodeSortableColumnEnum.MaximumDistanceWithAccess;
      break;
    case "maxDistWithout":
      sortedColumn = ZipCodeSortableColumnEnum.MaximumDistanceWithoutAccess;
      break;
    case "avgTime2":
      sortedColumn = ZipCodeSortableColumnEnum.AverageTime2;
      break;
    case "avgTimeWith":
      sortedColumn = ZipCodeSortableColumnEnum.AverageTimeWithAccess;
      break;
    case "avgTimeWithout":
      sortedColumn = ZipCodeSortableColumnEnum.AverageTimeWithoutAccess;
      break;
    case "maxTime2":
      sortedColumn = ZipCodeSortableColumnEnum.MaximumTime2;
      break;
    case "maxTimeWith":
      sortedColumn = ZipCodeSortableColumnEnum.MaximumTimeWithAccess;
      break;
    case "maxTimeWithout":
      sortedColumn = ZipCodeSortableColumnEnum.MaximumTimeWithoutAccess;
      break;
    case "avgDistWith2":
      sortedColumn = ZipCodeSortableColumnEnum.AverageDistanceWithAccess2;
      break;
    case "avgDistWithout2":
      sortedColumn = ZipCodeSortableColumnEnum.AverageDistanceWithoutAccess2;
      break;
    case "maxDistWith2":
      sortedColumn = ZipCodeSortableColumnEnum.MaximumDistanceWithAccess2;
      break;
    case "maxDistWithout2":
      sortedColumn = ZipCodeSortableColumnEnum.MaximumDistanceWithoutAccess2;
      break;
    case "avgTimeWith2":
      sortedColumn = ZipCodeSortableColumnEnum.AverageTimeWithAccess2;
      break;
    case "avgTimeWithout2":
      sortedColumn = ZipCodeSortableColumnEnum.AverageTimeWithoutAccess2;
      break;
    case "maxTimeWith2":
      sortedColumn = ZipCodeSortableColumnEnum.MaximumTimeWithAccess2;
      break;
    case "maxTimeWithout2":
      sortedColumn = ZipCodeSortableColumnEnum.MaximumTimeWithoutAccess2;
      break;
    case "withDistPercent":
      sortedColumn = ZipCodeSortableColumnEnum.WithAccessByDistancePercent;
      break;
    case "withTimePercent":
      sortedColumn = ZipCodeSortableColumnEnum.WithAccessByTimePercent;
      break;
    case "marketAccessPercent":
      sortedColumn = ZipCodeSortableColumnEnum.MarketAccessPercent;
      break;
    case "marketPercent":
      sortedColumn = ZipCodeSortableColumnEnum.MarketPercent;
      break;
    case "marketServicing":
      sortedColumn = ZipCodeSortableColumnEnum.MarketServicing;
      break;
    case "membershipType":
      sortedColumn = ZipCodeSortableColumnEnum.MembershipType;
      break;
    case "inZipProviderCount":
      sortedColumn = ZipCodeSortableColumnEnum.InZipProviderCount;
      break;
    case "inZipMarketProviderCount":
      sortedColumn = ZipCodeSortableColumnEnum.InZipMarketProviderCount;
      break;
    case "servicingProviderToMemberRatio":
      sortedColumn = ZipCodeSortableColumnEnum.ServicingProviderToMemberRatio;
      break;
    case "gapFillStatus":
      sortedColumn = ZipCodeSortableColumnEnum.GapFillStatus;
      break;
    case "gapFillStatusUpdatedByName":
      sortedColumn = ZipCodeSortableColumnEnum.GapFillStatusUpdatedByName;
      break;
    case "gapFillStatusUpdatedDate":
      sortedColumn = ZipCodeSortableColumnEnum.GapFillStatusUpdatedDate;
      break;
    case "gapAgeDays":
      sortedColumn = ZipCodeSortableColumnEnum.GapAgeDays;
      break;
  }
  return sortedColumn;
}

const getZipClassSortedColumnFromEnum = (newColumn: string): ZipClassSortableColumnEnum => {
  let sortedColumn: ZipClassSortableColumnEnum;
  switch (newColumn) {
    case "zipClass":
      sortedColumn = ZipClassSortableColumnEnum.ZipClass;
      break;
    case "specialty":
      sortedColumn = ZipClassSortableColumnEnum.SpecialtyGroupCode;
      break;
    case "minProviderCount":
      sortedColumn = ZipClassSortableColumnEnum.MinProviderCount;
      break;
    case "reqDist":
      sortedColumn = ZipClassSortableColumnEnum.ReqDist;
      break;
    case "reqTime":
      sortedColumn = ZipClassSortableColumnEnum.ReqTime;
      break;
    case "minPassPercent":
      sortedColumn = ZipClassSortableColumnEnum.MinPassPercent;
      break;
    case "membershipCount":
      sortedColumn = ZipClassSortableColumnEnum.MembershipCount;
      break;
    case "providerCount":
      sortedColumn = ZipClassSortableColumnEnum.ProviderCount;
      break;
    case "memberCountWith":
      sortedColumn = ZipClassSortableColumnEnum.MemberCountWithAccess;
      break;
    case "withPercent":
      sortedColumn = ZipClassSortableColumnEnum.WithAccessPercent;
      break;
    case "memberCountWithout":
      sortedColumn = ZipClassSortableColumnEnum.MemberCountWithoutAccess;
      break;
    case "withoutPercent":
      sortedColumn = ZipClassSortableColumnEnum.WithoutAccessPercent;
      break;
    case "metOverall":
      sortedColumn = ZipClassSortableColumnEnum.MetOverall;
      break;
    case "avgDist":
      sortedColumn = ZipClassSortableColumnEnum.AverageDistance;
      break;
    case "avgDist2":
      sortedColumn = ZipClassSortableColumnEnum.AverageDistance2;
      break;
    case "avgDistWith":
      sortedColumn = ZipClassSortableColumnEnum.AverageDistanceWithAccess;
      break;
    case "avgDistWithout":
      sortedColumn = ZipClassSortableColumnEnum.AverageDistanceWithoutAccess;
      break;
    case "exceptions":
      sortedColumn = ZipClassSortableColumnEnum.SpecialtyGroupExceptionsCount;
      break;
    case "maxDist":
      sortedColumn = ZipClassSortableColumnEnum.MaximumDistance;
      break;
    case "maxDist2":
      sortedColumn = ZipClassSortableColumnEnum.MaximumDistance2;
      break;
    case "maxDistWith":
      sortedColumn = ZipClassSortableColumnEnum.MaximumDistanceWithAccess;
      break;
    case "maxDistWithout":
      sortedColumn = ZipClassSortableColumnEnum.MaximumDistanceWithoutAccess;
      break;
    case "avgTime":
      sortedColumn = ZipClassSortableColumnEnum.AverageTime;
      break;
    case "avgTime2":
      sortedColumn = ZipClassSortableColumnEnum.AverageTime2;
      break;
    case "avgTimeWith":
      sortedColumn = ZipClassSortableColumnEnum.AverageTimeWithAccess;
      break;
    case "avgTimeWithout":
      sortedColumn = ZipClassSortableColumnEnum.AverageTimeWithoutAccess;
      break;
    case "maxTime":
      sortedColumn = ZipClassSortableColumnEnum.MaximumTime;
      break;
    case "maxTime2":
      sortedColumn = ZipClassSortableColumnEnum.MaximumTime2;
      break;
    case "maxTimeWith":
      sortedColumn = ZipClassSortableColumnEnum.MaximumTimeWithAccess;
      break;
    case "maxTimeWithout":
      sortedColumn = ZipClassSortableColumnEnum.MaximumTimeWithoutAccess;
      break;
    case "avgDistWith2":
      sortedColumn = ZipClassSortableColumnEnum.AverageDistanceWithAccess2;
      break;
    case "avgDistWithout2":
      sortedColumn = ZipClassSortableColumnEnum.AverageDistanceWithoutAccess2;
      break;
    case "maxDistWith2":
      sortedColumn = ZipClassSortableColumnEnum.MaximumDistanceWithAccess2;
      break;
    case "maxDistWithout2":
      sortedColumn = ZipClassSortableColumnEnum.MaximumDistanceWithoutAccess2;
      break;
    case "avgTimeWith2":
      sortedColumn = ZipClassSortableColumnEnum.AverageTimeWithAccess2;
      break;
    case "avgTimeWithout2":
      sortedColumn = ZipClassSortableColumnEnum.AverageTimeWithoutAccess2;
      break;
    case "maxTimeWith2":
      sortedColumn = ZipClassSortableColumnEnum.MaximumTimeWithAccess2;
      break;
    case "maxTimeWithout2":
      sortedColumn = ZipClassSortableColumnEnum.MaximumTimeWithoutAccess2;
      break;
    case "withDistPercent":
      sortedColumn = ZipClassSortableColumnEnum.WithAccessByDistancePercent;
      break;
    case "withTimePercent":
      sortedColumn = ZipClassSortableColumnEnum.WithAccessByTimePercent;
      break;
    case "marketAccessPercent":
      sortedColumn = ZipClassSortableColumnEnum.MarketAccessPercent;
      break;
    case "marketPercent":
      sortedColumn = ZipClassSortableColumnEnum.MarketPercent;
      break;
    case "marketServicing":
      sortedColumn = ZipClassSortableColumnEnum.MarketServicing;
      break;
    case "membershipType":
      sortedColumn = ZipClassSortableColumnEnum.MembershipType;
      break;
    case "inClassProviderCount":
      sortedColumn = ZipClassSortableColumnEnum.InClassProviderCount;
      break;
    case "inClassMarketProviderCount":
      sortedColumn = ZipClassSortableColumnEnum.InClassMarketProviderCount;
      break;
    case "servicingProviderToMemberRatio":
      sortedColumn = ZipClassSortableColumnEnum.ServicingProviderToMemberRatio;
      break;
  }
  return sortedColumn;
}

export const getSortedColumnFromEnum = (newColumn: string, perspective: ProjectPerspective): ZipCodeSortableColumnEnum | ZipClassSortableColumnEnum => {
  switch (perspective) {
    case ProjectPerspective.ByZipClass:
      return getZipClassSortedColumnFromEnum(newColumn);
    case ProjectPerspective.ByZipCode:
      return getZipCodeSortedColumnFromEnum(newColumn);
  }
}

export const printNumericString = (value: number, numberOfDecimalPlaces: number = 1): string => {
  return value !== undefined ? value.toFixed(numberOfDecimalPlaces).toLocaleString() : "---";
}

export const printPercentString = (value: number): string => {
  return value !== undefined ? `${value.toFixed(1)}` : "---";
}

export const getZipTimeString = (runConfiguration: ProjectRunConfiguration): string => {
  return runConfiguration.isProjected ? "Projected"
    : runConfiguration.isProjectedTermed ? "Projected-Termed"
      : runConfiguration.isTermed ? "Termed"
        : runConfiguration.isLatestPublished ? `Latest Published (${formatDateTime(runConfiguration.dateCreated)})`
          : !runConfiguration.datePublished ? `Unpublished (${formatDateTime(runConfiguration.dateCreated)})`
            : formatDateTime(runConfiguration.dateCreated);
}

export const sortDropDownItems = (a: ProjectRunConfiguration, b: ProjectRunConfiguration): number => {
  const getRank = (prc: ProjectRunConfiguration) => {
    if (prc.isProjected) return 1;
    if (prc.isTermed) return 2;
    if (prc.isProjectedTermed) return 3;
    return 0;
  };
  const rankA = getRank(a);
  const rankB = getRank(b);

  if (rankA !== rankB) {
    return rankA - rankB;
  }

  return a.dateCreated > b.dateCreated ? -1 : 1;
}

export const sortTrendChartItem = (a: ZipCodeTrendChartItem | ZipClassTrendChartItem, b: ZipCodeTrendChartItem | ZipClassTrendChartItem): number => {
  const getRank = (tci: ZipCodeTrendChartItem | ZipClassTrendChartItem) => {
    if (tci.itemType === TrendChartItemType.Projected) return 1;
    if (tci.itemType === TrendChartItemType.Termed) return 2;
    if (tci.itemType === TrendChartItemType.ProjectedTermed) return 3;
    return 0;
  };
  const rankA = getRank(a);
  const rankB = getRank(b);

  if (rankA !== rankB) {
    return rankA - rankB;
  }

  return new Date(a.dateCreatedOrProjected) < new Date(b.dateCreatedOrProjected) ? -1 : 1;
}

export const getResultsForLabel = (runConfiguration: ProjectRunConfiguration): string => {
  if (runConfiguration?.isProjected)
    return "Projected";
  else if (runConfiguration?.isProjectedTermed)
    return "Projected-Termed";
  else if (runConfiguration?.isTermed)
    return "Termed";
  else
    return formatDateTime(runConfiguration?.dateCreated).toString();
}

export const blankZipCodeRow: ZipCodeTableData = {
  [wafflesKeyColumnName]: null,
  accessTelehealthCredit: "---",
  avgDist: "---",
  avgDist2: "---",
  avgDistWith: "---",
  avgDistWith2: "---",
  avgDistWithout: "---",
  avgDistWithout2: "---",
  avgTime: "---",
  avgTime2: "---",
  avgTimeWith: "---",
  avgTimeWith2: "---",
  avgTimeWithout: "---",
  avgTimeWithout2: "---",
  city: "---",
  county: "---",
  gapAgeDays: "---",
  gapFillStatus: "---",
  gapFillStatusUpdatedByName: "---",
  gapFillStatusUpdatedDate: "---",
  inZipMarketProviderCount: "---",
  inZipProviderCount: "---",
  isInformationalSpecialty: false,
  marketAccessPercent: "---",
  marketPercent: "---",
  marketServicing: "---",
  maxDist: "---",
  maxDist2: "---",
  maxDistWith: "---",
  maxDistWith2: "---",
  maxDistWithout: "---",
  maxDistWithout2: "---",
  maxTime: "---",
  maxTime2: "---",
  maxTimeWith: "---",
  maxTimeWith2: "---",
  maxTimeWithout: "---",
  maxTimeWithout2: "---",
  memberCountWithAccess: "---",
  memberCountWithoutAccess: "---",
  membershipCount: "---",
  membershipType: "---",
  metDist: "---",
  metPassPercent: "---",
  metTime: "---",
  minPassPercent: "---",
  minProviderCount: "---",
  providerCount: "---",
  reqDist: "---",
  reqTime: "---",
  resultsFor: "---",
  servicingProviderToMemberRatio: "---",
  specialty: "---",
  state: "---",
  withAccessPercent: "---",
  withDistPercent: "---",
  withoutAccessPercent: "---",
  withTimePercent: "---",
  zipClass: "---",
  zipCode: undefined,
};

export const blankZipClassRow: ZipClassTableData = {
  [wafflesKeyColumnName]: null,
  accessTelehealthCredit: "---",
  avgDist: "---",
  avgDist2: "---",
  avgDistWith: "---",
  avgDistWith2: "---",
  avgDistWithout: "---",
  avgDistWithout2: "---",
  avgTime: "---",
  avgTime2: "---",
  avgTimeWith: "---",
  avgTimeWith2: "---",
  avgTimeWithout: "---",
  avgTimeWithout2: "---",
  exceptions: "---",
  inClassMarketProviderCount: "---",
  inClassProviderCount: "---",
  isInformationalSpecialty: false,
  marketAccessPercent: "---",
  marketPercent: "---",
  marketServicing: "---",
  maxDist: "---",
  maxDist2: "---",
  maxDistWith: "---",
  maxDistWith2: "---",
  maxDistWithout: "---",
  maxDistWithout2: "---",
  maxTime: "---",
  maxTime2: "---",
  maxTimeWith: "---",
  maxTimeWith2: "---",
  maxTimeWithout: "---",
  maxTimeWithout2: "---",
  memberCountWith: "---",
  memberCountWithout: "---",
  membershipCount: "---",
  membershipType: "---",
  metDist: "---",
  metOverall: "---",
  metTime: "---",
  minPassPercent: "---",
  minProviderCount: "---",
  providerCount: "---",
  reqDist: "---",
  reqTime: "---",
  resultsFor: "---",
  servicingProviderToMemberRatio: "---",
  specialty: "---",
  withDistPercent: "---",
  withoutPercent: "---",
  withPercent: "---",
  withTimePercent: "---",
  zipClass: "---",
}

/**
 * A function to take two sets of data and, if there are any key discrepancies (e.g. the service area changed between runs),
 * it will fill in that key with a blank row for presentation purposes.
 */
export const fillInMissingCompareModeRows = <T extends ZipCodeTableData | ZipClassTableData,>(records1: T[], records2: T[], blankRecord: T, timestamp1: string, timestamp2: string): [T[], T[]] => {
  if (!records1 || !records2) {
    return [records1, records2];
  }

  const newRecords1 = [...records1];
  const newRecords2 = [...records2];
  const keys1 = records1.map((r) => r[wafflesKeyColumnName]);
  const keys2 = records2.map((r) => r[wafflesKeyColumnName]);
  const exclusiveKeys1 = keys1.filter((k) => !keys2.includes(k));
  const exclusiveKeys2 = keys2.filter((k) => !keys1.includes(k));

  exclusiveKeys1.forEach((k) => {
    const newBlank = { ...blankRecord, resultsFor: timestamp2, [wafflesKeyColumnName]: k, };
    newRecords2.push(newBlank);
  })

  exclusiveKeys2.forEach((k) => {
    const newBlank = { ...blankRecord, [wafflesKeyColumnName]: k };
    const matchingRecord = records2.find((r) => r.key === k) as ZipCodeTableData;
    if (newRecords1.length === 0 || !!(newRecords1[0] as ZipCodeTableData).zipCode) {
      const newZipCodeBlank: ZipCodeTableData = {
        ...newBlank as ZipCodeTableData,
        city: matchingRecord.city,
        county: matchingRecord.county,
        resultsFor: timestamp1,
        specialty: matchingRecord.specialty,
        state: matchingRecord.state,
        zipClass: matchingRecord.zipClass,
        zipCode: matchingRecord.zipCode,
      };
      newRecords1.push(newZipCodeBlank as T);
    } else {
      const newZipClassBlank: ZipClassTableData = {
        ...newBlank as ZipClassTableData,
        resultsFor: timestamp1,
        specialty: matchingRecord.specialty,
        zipClass: matchingRecord.zipClass,
      };
      newRecords1.push(newZipClassBlank as T);
    }
  })

  return [newRecords1, newRecords2];
}

// The top configuration is the older of the two in compare mode, or the primary membership in dual membership
// The bottom configuration is the newer of the two in compare mode, or the secondary membership in dual membership
export const getTopAndBottomRunConfigurations = (
  runConfig1: ProjectRunConfiguration,
  runConfig2: ProjectRunConfiguration,
  isCompareMode: boolean,
  isDualMembershipMode: boolean,
): { bottomConfig: ProjectRunConfiguration, topConfig: ProjectRunConfiguration } => {
  let topConfig, bottomConfig;
  if (!isCompareMode && !isDualMembershipMode) {
    return { bottomConfig: undefined, topConfig: runConfig1 };
  }
  if (isCompareMode) {
    if (runConfig1.dateCreated > runConfig2.dateCreated) {
      topConfig = runConfig2;
      bottomConfig = runConfig1;
    } else {
      topConfig = runConfig1;
      bottomConfig = runConfig2;
    }
  } else if (isDualMembershipMode) {
    // the API is expected to always return the primary membership config as item 0, and the secondary as item 1
    topConfig = runConfig1;
    bottomConfig = runConfig2;
  }

  return { bottomConfig, topConfig: topConfig };
}

export const haveFilterSelectionsChanged = (filterSelections1: ZipAnalysisSearchSelections, filterSelections2: ZipAnalysisSearchSelections): boolean => {
  return (
    filterSelections1.city !== filterSelections2.city
    || filterSelections1.county.key !== filterSelections2.county.key
    || filterSelections1.metOverall.key !== filterSelections2.metOverall.key
    || filterSelections1.metPassPercent.key !== filterSelections2.metPassPercent.key
    || filterSelections1.specialtyGroupCode.key !== filterSelections2.specialtyGroupCode.key
    || filterSelections1.state.key !== filterSelections2.state.key
    || filterSelections1.zipClass.key !== filterSelections2.zipClass.key
    || filterSelections1.zipCode !== filterSelections2.zipCode
  );
}

// if we're in single membership mode, but the second membership is the one selected, we want to use the "compared" details instead
export const shouldUseComparedZipDetails = (isDualMembership: boolean, isCompareMode: boolean, topConfigType: ProjectRunConfigurationType): boolean => {
  return !isDualMembership && !isCompareMode && topConfigType === ProjectRunConfigurationType.DualMembershipSupplemental;
}

export const getSpecialtyExceptionKey = (exception: SpecialtyGroupException): string => {
  return `${exception.regionSetId}:${exception.specialtyGroupCode}`;
}

// This is the search parameters that are injected into the URL
export const getUrlSearchString = (
  filterSelections: ZipAnalysisSearchSelections,
  adequacyFilters: AdequacyFilters,
  runConfigurationId?: number,
  compareToRunConfigurationId?: number,
): string => {
  return querystringify({
    changedGaps: adequacyFilters.changedGaps,
    city: filterSelections.city === "" ? null : filterSelections.city,
    compareTo: compareToRunConfigurationId ?? null,
    county: filterSelections.county.key === "all" ? null : filterSelections.county.key,
    metOverall: filterSelections.metOverall.key === "all" ? null : filterSelections.metOverall.key,
    metPassPercent: filterSelections.metPassPercent.key === "all" ? null : filterSelections.metPassPercent.key,
    resultsAsOf: runConfigurationId ?? null,
    specialtyGroupCode: filterSelections.specialtyGroupCode.key === "all" ? null : filterSelections.specialtyGroupCode.key,
    state: filterSelections.state.key === "all" ? null : filterSelections.state.key,
    viewBy: adequacyFilters.viewBy || null,
    zipClass: filterSelections.zipClass.key === "all" ? null : filterSelections.zipClass.key,
    zipCode: filterSelections.zipCode === "" ? null : filterSelections.zipCode,
  });
}

// Resolves the primary and compare run configurations encoded in the URL search parameters
// (resultsAsOf / compareTo). Returns undefined for either when the parameter is missing or
// does not match a run configuration in the project (e.g. filtered out by feature flags or role).
export const getRunConfigurationsFromSearchParams = (
  searchParams: { [key: string]: string },
  projectRunConfigurations: ProjectRunConfiguration[],
): { runConfiguration?: ProjectRunConfiguration, compareToRunConfiguration?: ProjectRunConfiguration } => {
  const findById = (value: string): ProjectRunConfiguration | undefined => {
    if (!value) {
      return undefined;
    }
    const id = parseInt(value, 10);
    if (isNaN(id)) {
      return undefined;
    }
    return (projectRunConfigurations || []).find((prc) => prc.projectRunConfigurationId === id);
  };

  return {
    compareToRunConfiguration: findById(searchParams.compareTo),
    runConfiguration: findById(searchParams.resultsAsOf),
  };
}

// and this is the SearchParameters object that is sent to the API to request new data
export const getSearchParametersFromFilterSelections = (filterSelections: ZipAnalysisSearchSelections): ZipAnalysisSearchParameters => {
  return {
    city: filterSelections.city === "" ? null : filterSelections.city,
    county: filterSelections.county.key === "all" ? null : filterSelections.county.key,
    metOverall: filterSelections.metOverall.key === "all"
      ? null
      : filterSelections.metOverall.key.toLocaleLowerCase() === "yes"
        ? true
        : false,
    metPassPercent: filterSelections.metPassPercent.key === "all"
      ? null
      : filterSelections.metPassPercent.key.toLocaleLowerCase() === "yes"
        ? true
        : false,
    specialtyGroupCode: filterSelections.specialtyGroupCode.key === "all" ? null : filterSelections.specialtyGroupCode.key,
    state: filterSelections.state.key === "all" ? null : filterSelections.state.key,
    zipClass: filterSelections.zipClass.key === "all" ? null : filterSelections.zipClass.key,
    zipCode: filterSelections.zipCode === "" ? null : filterSelections.zipCode,
  }
};

export const getSearchParametersWithPrimaryCounty = (
  filterSelections: ZipAnalysisSearchSelections,
  zipDetails: ZipCodeDisplayModel[]
): ZipAnalysisSearchParameters => {
  const params = getSearchParametersFromFilterSelections(filterSelections);
  if (params.zipCode && !params.county && !params.state) {
    const primaryCounty = zipDetails?.find(r => r.zipCode === params.zipCode)?.county;
    if (primaryCounty) return { ...params, county: primaryCounty };
  }
  return params;
};
